// Una banda de fecha: cabecera plegable + las cargas de ese bucket.
//
// `atrasadas` arranca COLAPSADA cuando pasa de 100 filas: es la banda que más
// crece —vive de lo que nadie ha movido— y desplegarla siempre convertiría la
// pantalla en un muro de filas rojas antes de ver nada más. El resto de bandas
// arrancan abiertas: son las que hay que mirar hoy.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { GrupoFecha } from '@/utils/agruparPorFecha'
import type { CargaSalida, MarcasFilas } from '@/types'
import { FilaCargaSalida } from './FilaCargaSalida'
import type { ColumnaCargaSalida } from './columnasCargaSalida'
import { fmtRangoBanda } from './formatoSalida'

/** A partir de cuántas filas la banda de atrasadas arranca colapsada. */
const UMBRAL_COLAPSO = 100

interface Props {
  grupo: GrupoFecha<CargaSalida>
  dark: boolean
  columnas: ColumnaCargaSalida[]
  hoy: Date
  marcas: MarcasFilas
  claveDe: (c: CargaSalida) => string
  abiertaDe: (carga: string) => boolean
  onToggleFila: (carga: string) => void
  onFicha: (carga: string) => void
}

export function GrupoFechaBanda({
  grupo, dark, columnas, hoy, marcas, claveDe, abiertaDe, onToggleFila,
  onFicha,
}: Props) {
  const { t } = useTranslation()
  const [colapsada, setColapsada] = useState(
    grupo.bucket === 'atrasadas' && grupo.items.length > UMBRAL_COLAPSO,
  )

  // Cabecera de banda en azul fuerte con texto blanco: dirección pidió que las
  // bandas de fecha se vean al recorrer la tabla. Las ATRASADAS conservan su
  // semántica en rojo (el azul no puede decir «va tarde»), también sólido.
  const atrasadas = grupo.bucket === 'atrasadas'
  const bandaBg = atrasadas
    ? (dark ? 'bg-red-800' : 'bg-red-700')
    : (dark ? 'bg-blue-800' : 'bg-blue-700')
  const bandaHover = atrasadas
    ? (dark ? 'hover:bg-red-700' : 'hover:bg-red-600')
    : (dark ? 'hover:bg-blue-700' : 'hover:bg-blue-600')
  const bandaTxt = 'text-white'
  const mutedTxt = 'text-white/80'

  const rango = grupo.desde && grupo.hasta ? fmtRangoBanda(grupo.desde, grupo.hasta) : null

  return (
    <tbody>
      <tr className={`border-t-2 ${dark ? 'border-white/10' : 'border-slate-300'}`}>
        <td colSpan={columnas.length + 2} className="p-0">
          <button
            type="button"
            onClick={() => setColapsada(v => !v)}
            aria-expanded={!colapsada}
            className={`w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer transition-colors ${bandaBg} ${bandaHover}`}
          >
            <svg
              className={`w-3 h-3 shrink-0 transition-transform duration-150 ${colapsada ? '' : 'rotate-90'} ${mutedTxt}`}
              viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M5 11l4-4-4-4" />
            </svg>
            <span className={`text-[11px] font-bold uppercase tracking-wider ${bandaTxt}`}>
              {t(`cargasSalida.bandas.${grupo.bucket}`)}
            </span>
            {rango && (
              <span className={`text-[10px] font-semibold ${mutedTxt}`}>· {rango}</span>
            )}
            <span className={`text-[11px] font-semibold tabular-nums ${mutedTxt}`}>
              {colapsada
                ? t('cargasSalida.bandas.verMas', { count: grupo.items.length })
                : t('cargasSalida.bandas.recuento', { count: grupo.items.length })}
            </span>
            {grupo.bucket === 'atrasadas' && grupo.diasRetrasoMax != null && (
              <span className={`text-[10px] font-semibold ${bandaTxt}`}>
                · {t('cargasSalida.bandas.retrasoMax', { count: grupo.diasRetrasoMax })}
              </span>
            )}
          </button>
        </td>
      </tr>

      {!colapsada && grupo.items.map(c => {
        const clave = claveDe(c)
        return (
          <FilaCargaSalida
            key={clave}
            c={c}
            dark={dark}
            columnas={columnas}
            hoy={hoy}
            abierta={abiertaDe(c.carga)}
            onToggle={() => onToggleFila(c.carga)}
            marcas={marcas}
            clave={clave}
            onFicha={() => onFicha(c.carga)}
          />
        )
      })}
    </tbody>
  )
}
